'use client';

import appShellStore from '@/lib/offline/appShellStore';
import type {
  WorkItem,
  WorkflowState,
  WorkflowStateRecord,
} from '@/app/client-zone/modules/total-operations/components/smart-task/types';
import type {
  AttendanceSession,
  DailySummary,
} from '@/components/attendance/types/attendance.types';

const RUNTIME_SNAPSHOT_VERSION = 1;
const RUNTIME_SNAPSHOT_PREFIX = `operations_runtime_snapshot:v${RUNTIME_SNAPSHOT_VERSION}:user:`;

export const OPERATIONS_RUNTIME_SNAPSHOT_UPDATED_EVENT = 'operations-runtime-snapshot-updated';

export type AppointmentRuntimeTimerState = 'en_route' | 'in_progress';
export type OperationsRuntimeAttendanceStatus = 'clocked_out' | 'clocked_in' | 'on_break';
export type OperationsRuntimeAttendanceSource = 'face_scan' | 'whatsapp' | 'offline' | 'api' | 'websocket' | 'manual';

export interface OperationsAppointmentRuntimeTimer {
  appointmentId: string;
  activeState: AppointmentRuntimeTimerState;
  startedAt: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  userId?: number;
  userName?: string;
  elapsedOffsetSeconds?: number;
  isPaused?: boolean;
  updatedAt: string;
  source?: 'websocket' | 'seed' | 'workflow' | 'manual';
}

export interface OperationsRuntimeWorkflowContext {
  activeWorkItemId: string | null;
  travelingToWorkItemId: string | null;
  activeWorkItemType?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  userId?: number | null;
  userName?: string | null;
}

export interface OperationsRuntimeAttendanceState {
  employeeId: number;
  status: OperationsRuntimeAttendanceStatus;
  lastAction?: string | null;
  lastActionTime?: string | null;
  statusLabel?: string | null;
  totalHours?: string | null;
  breakTime?: string | null;
  sessions?: number | null;
  clockInTime?: string | null;
  clockOutTime?: string | null;
  onBreakSince?: string | null;
  currentSession?: {
    id: AttendanceSession['id'];
    employeeId: AttendanceSession['employee_id'];
    clockInTime: AttendanceSession['clock_in_time'];
    clockOutTime?: AttendanceSession['clock_out_time'];
    totalHours?: AttendanceSession['total_hours'];
    status: AttendanceSession['status'];
    verificationMethod?: AttendanceSession['verification_method'];
    locationType?: AttendanceSession['location_type'];
    updatedAt?: AttendanceSession['updated_at'];
  } | null;
  todaySummary?: {
    date: DailySummary['date'];
    totalHours: DailySummary['total_hours'];
    breakTime: DailySummary['break_time'];
    sessionCount: DailySummary['session_count'];
    clockInTime?: DailySummary['clock_in_time'];
    clockOutTime?: DailySummary['clock_out_time'];
    status: DailySummary['status'];
    onBreakSince?: DailySummary['on_break_since'];
  } | null;
  updatedAt: string;
  source?: OperationsRuntimeAttendanceSource;
}

export interface OperationsRuntimeSnapshot {
  version: number;
  client_id: number;
  user_id: number;
  updated_at: string;
  workflow: WorkflowStateRecord | null;
  workflowContext: OperationsRuntimeWorkflowContext | null;
  attendance: OperationsRuntimeAttendanceState | null;
  appointmentTimers: Record<string, OperationsAppointmentRuntimeTimer>;
}

export interface OperationsRuntimeSnapshotUpdatedDetail {
  clientId: number;
  userId: number;
  source?: string;
  snapshot: OperationsRuntimeSnapshot;
}

function hasValidIdentity(clientId: number, userId: number): boolean {
  return Number.isFinite(clientId) && clientId > 0 && Number.isFinite(userId) && userId > 0;
}

function getRuntimeSnapshotKey(userId: number): string {
  return `${RUNTIME_SNAPSHOT_PREFIX}${userId}`;
}

function createEmptySnapshot(clientId: number, userId: number): OperationsRuntimeSnapshot {
  return {
    version: RUNTIME_SNAPSHOT_VERSION,
    client_id: clientId,
    user_id: userId,
    updated_at: new Date().toISOString(),
    workflow: null,
    workflowContext: null,
    attendance: null,
    appointmentTimers: {},
  };
}

function cloneAttendanceState(
  attendance: OperationsRuntimeAttendanceState | null | undefined,
): OperationsRuntimeAttendanceState | null {
  if (!attendance) {
    return null;
  }

  return {
    ...attendance,
    currentSession: attendance.currentSession
      ? { ...attendance.currentSession }
      : null,
    todaySummary: attendance.todaySummary
      ? { ...attendance.todaySummary }
      : null,
  };
}

function cloneSnapshot(snapshot: OperationsRuntimeSnapshot): OperationsRuntimeSnapshot {
  return {
    ...snapshot,
    workflow: snapshot.workflow
      ? {
          ...snapshot.workflow,
          metadata: { ...(snapshot.workflow.metadata || {}) },
          active_time_entries: Array.isArray(snapshot.workflow.active_time_entries)
            ? [...snapshot.workflow.active_time_entries]
            : snapshot.workflow.active_time_entries,
        }
      : null,
    workflowContext: snapshot.workflowContext
      ? { ...snapshot.workflowContext }
      : null,
    attendance: cloneAttendanceState(snapshot.attendance),
    appointmentTimers: { ...(snapshot.appointmentTimers || {}) },
  };
}

async function readSnapshot(clientId: number, userId: number): Promise<OperationsRuntimeSnapshot> {
  if (!hasValidIdentity(clientId, userId)) {
    return createEmptySnapshot(clientId, userId);
  }

  const stored = await appShellStore.getJson<OperationsRuntimeSnapshot & Record<string, unknown>>(clientId, getRuntimeSnapshotKey(userId));
  if (!stored || typeof stored !== 'object') {
    return createEmptySnapshot(clientId, userId);
  }

  return {
    ...createEmptySnapshot(clientId, userId),
    ...stored,
    client_id: clientId,
    user_id: userId,
    workflowContext: stored.workflowContext ? { ...stored.workflowContext } : null,
    attendance: cloneAttendanceState(stored.attendance),
    appointmentTimers: { ...(stored.appointmentTimers || {}) },
  };
}

async function writeSnapshot(
  clientId: number,
  userId: number,
  snapshot: OperationsRuntimeSnapshot,
  source?: string,
): Promise<OperationsRuntimeSnapshot> {
  const nextSnapshot: OperationsRuntimeSnapshot = {
    ...snapshot,
    version: RUNTIME_SNAPSHOT_VERSION,
    client_id: clientId,
    user_id: userId,
    updated_at: new Date().toISOString(),
    workflowContext: snapshot.workflowContext ? { ...snapshot.workflowContext } : null,
    attendance: cloneAttendanceState(snapshot.attendance),
    appointmentTimers: { ...(snapshot.appointmentTimers || {}) },
  };

  await appShellStore.upsertJson(clientId, getRuntimeSnapshotKey(userId), nextSnapshot as unknown as Record<string, unknown>);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<OperationsRuntimeSnapshotUpdatedDetail>(OPERATIONS_RUNTIME_SNAPSHOT_UPDATED_EVENT, {
      detail: {
        clientId,
        userId,
        source,
        snapshot: nextSnapshot,
      },
    }));
  }

  return nextSnapshot;
}

async function mutateSnapshot(params: {
  clientId: number;
  userId: number;
  source?: string;
  mutate: (snapshot: OperationsRuntimeSnapshot) => void;
}): Promise<OperationsRuntimeSnapshot | null> {
  const { clientId, userId, source, mutate } = params;
  if (!hasValidIdentity(clientId, userId)) {
    return null;
  }

  const snapshot = cloneSnapshot(await readSnapshot(clientId, userId));
  mutate(snapshot);
  return writeSnapshot(clientId, userId, snapshot, source);
}

function buildUserName(userName?: string | null): string | undefined {
  const normalized = typeof userName === 'string' ? userName.trim() : '';
  return normalized.length > 0 ? normalized : undefined;
}

function resolveWorkflowTimerState(currentState: WorkflowState): AppointmentRuntimeTimerState | null {
  if (currentState === 'traveling') return 'en_route';
  if (currentState === 'checked_in' || currentState === 'paused' || currentState === 'completing') {
    return 'in_progress';
  }
  return null;
}

function buildWorkflowDerivedTimer(snapshot: OperationsRuntimeSnapshot): OperationsAppointmentRuntimeTimer | null {
  const workflow = snapshot.workflow;
  const context = snapshot.workflowContext;
  if (!workflow || !context || context.activeWorkItemType !== 'appointment') {
    return null;
  }

  const activeState = resolveWorkflowTimerState(workflow.current_state);
  if (!activeState) {
    return null;
  }

  const appointmentId = workflow.traveling_to_work_item_id || workflow.active_work_item_id || context.activeWorkItemId || context.travelingToWorkItemId;
  if (!appointmentId) {
    return null;
  }

  const startedAt = activeState === 'en_route'
    ? workflow.travel_started_at
    : (workflow.work_started_at || workflow.state_started_at);

  if (!startedAt) {
    return null;
  }

  const travelMeta =
    workflow.metadata && typeof workflow.metadata === 'object' && !Array.isArray(workflow.metadata)
      ? (workflow.metadata as { accumulated_travel_seconds?: unknown })
      : undefined;
  const accumulatedTravel = travelMeta?.accumulated_travel_seconds;
  const elapsedOffsetSeconds =
    activeState === 'en_route'
      ? (typeof accumulatedTravel === 'number'
        ? accumulatedTravel
        : Number(accumulatedTravel)) || 0
      : Number(workflow.accumulated_work_seconds) || 0;

  return {
    appointmentId: String(appointmentId),
    activeState,
    startedAt,
    scheduledStart: context.scheduledStart || undefined,
    scheduledEnd: context.scheduledEnd || undefined,
    userId: context.userId ?? workflow.user_id,
    userName: buildUserName(context.userName),
    elapsedOffsetSeconds,
    isPaused: workflow.current_state === 'paused',
    updatedAt: snapshot.updated_at || new Date().toISOString(),
    source: 'workflow',
  };
}

export async function loadOperationsRuntimeSnapshot(clientId: number, userId: number): Promise<OperationsRuntimeSnapshot> {
  return readSnapshot(clientId, userId);
}

export async function upsertOperationsRuntimeAppointmentTimers(params: {
  clientId: number;
  userId: number;
  timers: OperationsAppointmentRuntimeTimer[];
  source?: string;
}): Promise<OperationsRuntimeSnapshot | null> {
  const { clientId, userId, timers, source } = params;
  const normalizedTimers = timers.filter((timer) => typeof timer?.appointmentId === 'string' && timer.appointmentId.trim().length > 0);
  if (normalizedTimers.length === 0) {
    return hasValidIdentity(clientId, userId)
      ? readSnapshot(clientId, userId)
      : null;
  }

  return mutateSnapshot({
    clientId,
    userId,
    source,
    mutate: (snapshot) => {
      for (const timer of normalizedTimers) {
        snapshot.appointmentTimers[timer.appointmentId] = {
          ...timer,
          appointmentId: String(timer.appointmentId),
          updatedAt: timer.updatedAt || new Date().toISOString(),
        };
      }
    },
  });
}

export async function removeOperationsRuntimeAppointmentTimers(params: {
  clientId: number;
  userId: number;
  appointmentIds: string[];
  source?: string;
}): Promise<OperationsRuntimeSnapshot | null> {
  const { clientId, userId, appointmentIds, source } = params;
  const ids = appointmentIds.map((appointmentId) => String(appointmentId)).filter(Boolean);
  if (ids.length === 0) {
    return hasValidIdentity(clientId, userId)
      ? readSnapshot(clientId, userId)
      : null;
  }

  return mutateSnapshot({
    clientId,
    userId,
    source,
    mutate: (snapshot) => {
      for (const appointmentId of ids) {
        delete snapshot.appointmentTimers[appointmentId];
      }
    },
  });
}

export async function saveOperationsRuntimeWorkflow(params: {
  clientId: number;
  userId: number;
  workflowState: WorkflowStateRecord | null;
  activeWorkItem?: Pick<WorkItem, 'id' | 'item_type' | 'scheduled_start' | 'scheduled_end'> | null;
  actor?: { userId?: number | null; userName?: string | null };
  source?: string;
}): Promise<OperationsRuntimeSnapshot | null> {
  const { clientId, userId, workflowState, activeWorkItem, actor, source } = params;

  return mutateSnapshot({
    clientId,
    userId,
    source,
    mutate: (snapshot) => {
      snapshot.workflow = workflowState
        ? {
            ...workflowState,
            metadata: { ...(workflowState.metadata || {}) },
            active_time_entries: Array.isArray(workflowState.active_time_entries)
              ? [...workflowState.active_time_entries]
              : workflowState.active_time_entries,
          }
        : null;
      snapshot.workflowContext = workflowState
        ? {
            activeWorkItemId: activeWorkItem?.id ? String(activeWorkItem.id) : (workflowState.active_work_item_id || null),
            travelingToWorkItemId: workflowState.traveling_to_work_item_id || null,
            activeWorkItemType: activeWorkItem?.item_type || null,
            scheduledStart: activeWorkItem?.scheduled_start || null,
            scheduledEnd: activeWorkItem?.scheduled_end || null,
            userId: actor?.userId ?? workflowState.user_id,
            userName: actor?.userName || null,
          }
        : null;

      const derivedTimer = buildWorkflowDerivedTimer(snapshot);
      if (derivedTimer) {
        snapshot.appointmentTimers[derivedTimer.appointmentId] = derivedTimer;
        return;
      }

      const idsToRemove = new Set<string>();
      if (activeWorkItem?.item_type === 'appointment' && activeWorkItem.id) {
        idsToRemove.add(String(activeWorkItem.id));
      }
      if (snapshot.workflowContext?.activeWorkItemType === 'appointment') {
        if (snapshot.workflowContext.activeWorkItemId) {
          idsToRemove.add(String(snapshot.workflowContext.activeWorkItemId));
        }
        if (snapshot.workflowContext.travelingToWorkItemId) {
          idsToRemove.add(String(snapshot.workflowContext.travelingToWorkItemId));
        }
      }

      for (const appointmentId of idsToRemove) {
        delete snapshot.appointmentTimers[appointmentId];
      }
    },
  });
}

export async function saveOperationsRuntimeAttendance(params: {
  clientId: number;
  userId: number;
  attendanceState: OperationsRuntimeAttendanceState | null;
  source?: string;
}): Promise<OperationsRuntimeSnapshot | null> {
  const { clientId, userId, attendanceState, source } = params;

  return mutateSnapshot({
    clientId,
    userId,
    source,
    mutate: (snapshot) => {
      snapshot.attendance = attendanceState
        ? {
            ...(snapshot.attendance || {}),
            ...attendanceState,
            currentSession: attendanceState.currentSession
              ? { ...attendanceState.currentSession }
              : attendanceState.currentSession === null
                ? null
                : snapshot.attendance?.currentSession || null,
            todaySummary: attendanceState.todaySummary
              ? { ...attendanceState.todaySummary }
              : attendanceState.todaySummary === null
                ? null
                : snapshot.attendance?.todaySummary || null,
            updatedAt: attendanceState.updatedAt || new Date().toISOString(),
          }
        : null;
    },
  });
}

export function getOperationsRuntimeAppointmentTimers(
  snapshot: OperationsRuntimeSnapshot | null | undefined,
  appointmentIds?: string[],
): Map<string, OperationsAppointmentRuntimeTimer> {
  const timers = new Map<string, OperationsAppointmentRuntimeTimer>();
  if (!snapshot) {
    return timers;
  }

  const allowedIds = appointmentIds && appointmentIds.length > 0
    ? new Set(appointmentIds.map((appointmentId) => String(appointmentId)))
    : null;

  const maybeAdd = (timer: OperationsAppointmentRuntimeTimer | null | undefined) => {
    if (!timer?.appointmentId) return;
    const appointmentId = String(timer.appointmentId);
    if (allowedIds && !allowedIds.has(appointmentId)) return;
    timers.set(appointmentId, {
      ...timer,
      appointmentId,
      updatedAt: timer.updatedAt || snapshot.updated_at || new Date().toISOString(),
    });
  };

  for (const timer of Object.values(snapshot.appointmentTimers || {})) {
    maybeAdd(timer);
  }

  const derivedTimer = buildWorkflowDerivedTimer(snapshot);
  if (derivedTimer) {
    maybeAdd(derivedTimer);
  }

  return timers;
}